/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 26-07-2026
 *
 * De verzamelplek voor norm-zaken: de normtelling en naamtelling over het werkmodel, de
 * worker-lifecycle van de montecarlo-matcher (generatiebewaking, terminate), de
 * interpretatie-/foutmodel-state en — sinds golf 3b — het foutoverzicht met de per-node
 * meldingen die eruit volgen. Port van de normmatch-plumbing uit `model.service.js` en van
 * `updateFoutweergave` uit de modelview-controller; deze service schrijft ook
 * `DlModel.normtelling` (voorheen de twee `_stuurNormtellingNg21`-pushes).
 * Heette tot golf 3b `NormmatchWorkerService`: de service stuurt de worker aan, hij ís hem niet.
 *
 * Sinds golf a3 stap 4 horen de triggers en hun sessie-guards er ook bij: `verwerkStand` (de
 * ng1-`wijzigHooks`), `maakNormmatch` en `verwerkNormdata`. Wat er ná de norm gebeurt is
 * nadrukkelijk niet van de norm — de norm **meldt**, en een ander doet ermee wat hij moet doen
 * (ontwerpvraag 5 van golf a3): de `OpslagService` logt `errormodel`/`errorcount` en stuurt de
 * telling naar het backend, de samenwerking stuurt de interpretaties naar de volgers. Zou de norm
 * dat zelf doen, dan injecteerde hij de opslagketen terwijl die hem al aanroept — een cyclus.
 * De werk-input komt bij elke trigger mee: de opslagketen bezit de stand, wij niet. Ook de
 * `help`-regel wordt hier alleen samengesteld; het versturen doet de `OpslagService` via
 * `registreerLogHelp`.
 */
import {computed, effect, inject, Injectable, signal, untracked, Signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import type {Core, NodeSingular} from 'cytoscape';
import {Sihwlog} from 'sihw-ngx';
import {elements} from 'dynalearn-common/lib/elements';
import {specialenaam} from '../../lib/specialenaam';
import {DlModel} from '../model/dl-model';
import type {Tekenaanleiding} from '../model/opslag.service';
import {GlobalModelcanvasService} from '../model/global-modelcanvas.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import {ProjectVlaggenService} from '../../services/project-vlaggen.service';
import {Melding} from '../../lib/melding';
import {type Feedbackherkomst, herkomstVanAanleiding} from '../../lib/feedbackherkomst';
import type {NormmatchElement, NormmatchElementen, NormmatchFoutdata, NormmatchFoutmodel, NormmatchInput, NormmatchInterpretatie, NormmatchOutput} from 'dynalearn-common/lib/normmatch.types';

/**
 * De telling per normtype: wat het normmodel verwacht, het getelde aantal in het werkmodel en het
 * aantal fouten uit de normmatch (null zolang er geen match is).
 *
 * `verwacht` is optioneel omdat het pas sinds golf a5 meegaat: een telling die van een oudere leider
 * over de lijn komt heeft hem niet. De normbalk valt dan terug op de normdefinitie. Bij een
 * replaystap is die terugval juist verkeerd — daar hoort de verwachting van tóen — en daarom staat
 * hij hier en niet alleen in `DlModel.norm.teller`.
 */
export type NormTypeTelling = Record<string, {verwacht?: number; aantal: number; fouten: number | null}>;

/**
 * De namentelling: hoeveel namen het normmodel verwacht en hoeveel er daadwerkelijk kloppen.
 */
export interface NaamTelling {
    norm: number;
    aantal: number;
}

/**
 * De werkmodel-data zoals de opslagketen hem aanlevert (`verwerkStand`): de `modelData()`-vorm
 * (`_data.model`), met de elementen op id en per type een array met ids.
 */
export interface WerkModelData {
    elements: NormmatchElementen;
    types: Record<string, unknown[]>;
}

/**
 * Eén element binnen een foutregel: het modelelement plus de ruwe foutcode waarvoor het hier
 * staat. Die foutcode hoort erbij omdat één element twee fouten kan hebben die naar dezelfde
 * vertaling resolven, en omdat een generieke vertaling (`any_<fout>`) elementen van verschillende
 * types met verschillende foutcodes in één regel zet: (id, foutcode) is de sleutel, de regel niet.
 */
export interface NormFoutElement {
    el: NormmatchElement;
    foutcode: string;
}

/**
 * Eén regel in het foutoverzicht: alle elementen die dezelfde vertaalde foutbeschrijving
 * opleveren.
 */
export interface NormFoutregel {
    /** De vertaalde beschrijving; mag opmaak bevatten. */
    tekst: string;
    /** De meest specifieke foutsleutel; naam gelijk aan die in het actielog. */
    fout: string;
    /** De daadwerkelijk gebruikte vertaalsleutel, voor de debugweergave. */
    gebruikt: string;
    elementen: NormFoutElement[];
}

/**
 * De plaats van een element-met-fout in het overzicht: de positie van de regel en van het element
 * daarbinnen. Dat is de index-ruimte waarin het actielog schrijft (`help`/`foutselect`).
 */
export interface FoutPositie {
    foutindex: number;
    elementindex: number;
}

/**
 * Eén normfeedback-melding voor een node (structureel gelijk aan `FeedbackMelding` van
 * `BaseFeedbackIconComponent`).
 */
export interface NormfeedbackMelding {
    type: string;
    tekst: string;
    /**
     * Of deze melding er bij de laatst binnengekomen uitslag bíj kwam. Hij hoort in de lijst en niet
     * in een boekhouding ernaast: dan valt er niets te synchroniseren tussen het moment waarop de
     * data binnenkomt en het moment waarop een icoon ernaar vraagt.
     */
    isNieuw: boolean;
}

/**
 * De payload van de `help`-actielogregel: het hele overzicht in de vorm die het backend al jaren
 * bewaart — de elementen als kale modelobjecten, zonder de foutcode ernaast.
 */
export interface HelpLogPayload {
    errors: Array<{tekst: string; fout: string; gebruikt: string; elementen: NormmatchElement[]}>;
}

/**
 * Eén regel uit het gefilterde foutmodel: een element met de fouten die de normmatch erbij vond.
 * Dit is de vorm die in de actielog terechtkomt (`errormodel`).
 */
export type NormFoutmodelregel = NormmatchFoutdata;

/**
 * Wat er met een rekenronde gebeurt. Vier standen, en ze dekken samen elke afloop af: er begint een
 * ronde, er begint er juist géén, er komt een uitslag, of de ronde wordt afgebroken.
 */
export type Normrondestand = 'gestart' | 'geenronde' | 'uitslag' | 'afgebroken';

/**
 * De melding over een rekenronde. Nadrukkelijk náást `bijNormdata`: die laatste is de wire-melding
 * waarmee de leider zijn volgers bijwerkt en draagt de úitslag; deze zegt alleen wát er met een ronde
 * gebeurt. De opslagketen leest er het rustsignaal uit: een snapshot wordt pas gemaakt als er geen
 * ronde meer loopt, zodat hij de normstand draagt die bij zijn eigen modelstand hoort.
 */
export interface Normrondemelding {
    stand: Normrondestand;
    /**
     * De modelstand waar deze melding over gaat. `null` als die niet te bepalen is — dan valt er
     * niets op te binden, en telt alleen de generatie.
     */
    standsleutel: string | null;
    /** De workergeneratie. Bij `geenronde` is er geen, want er draait niets. */
    generatie?: number;
    /** Bij `geenronde`: waarom er niet gerekend wordt. Alleen voor de log. */
    reden?: string;
}

/**
 * De normstand zoals hij in een snapshot meegaat: precies wat de weergave ervan leest, en niets
 * meer. De `mapping` per foutregel blijft eruit — die is tweederde van de bytes en de weergave raakt
 * hem nergens aan. De verwachte aantallen gaan wél mee, want zonder die zou een replaystap tegen de
 * norm van nú worden afgebeeld.
 */
export interface NormSnapshotblok {
    /** Per element met fouten: het element en zijn foutcodes. `null` als er geen match is. */
    foutmodel: Array<{el: NormmatchElement; fouten: string[]}> | null;
    /** Per normtype wat de norm verwacht, wat er staat en hoeveel ervan fout is. */
    telling: Record<string, {verwacht: number; aantal: number; fouten: number | null}>;
    /** De namentelling, met de verwachting erbij. */
    naamtelling: {verwacht: number; aantal: number};
}

/**
 * Een vastgelegde normstand, om later terug te zetten; zie `NormService.bewaarStand`.
 */
export interface BewaardeNormstand {
    interpretaties: NormmatchOutput;
    telling: {telling: NormTypeTelling; naamtelling: NaamTelling};
    vorigeFoutsleutels: Set<string> | null;
    /** De modelstand waar de uitslag bij hoort, of `null` als hij niet bij een geladen stand hoort. */
    standsleutel: string | null;
}

/**
 * Wat er gemeld wordt als er verse normdata is. `gewijzigdFoutmodel` staat er alleen als het
 * foutmodel daadwerkelijk veranderd is: dat is de aanleiding om een `errormodel`-regel te loggen.
 */
export interface Normdatamelding {
    interpretaties: NormmatchOutput;
    gewijzigdFoutmodel: NormFoutmodelregel[] | null;
    /**
     * De foutsleutels die bij déze uitslag nieuw zijn, als `<elementId>|<foutcode>`. Hoort bij de
     * melding en niet bij de servicestand: een melding die alleen een verse telling draagt heeft
     * geen nieuws, en de leider stuurt hem in die vorm ook naar zijn volgers.
     */
    nieuw: string[];
}

@Injectable({providedIn: 'root'})
export class NormService {

    private readonly log = new Sihwlog('debug', 'NormService');

    /**
     * Het modeldocument: bron van het `norm`-document en bestemming van de `normtelling`.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De canvas-eigenaar: bron van de verse `cy` voor de relatienamen en de parent-data in de
     * vertaalargumenten van het foutoverzicht.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    /**
     * De samenwerk-state: een volger logt niet zelf, hij krijgt de norminterpretatie van de leider.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * De projectvlaggen: of dit project de normfoutfeedback toont bepaalt of er überhaupt een
     * normmatch gemaakt wordt.
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    private readonly translateService = inject(TranslateService);

    /**
     * Er is verse normdata: de interpretaties, en of het foutmodel daarbij veranderd is. De
     * opslagketen logt, de samenwerking stuurt door — zie `bijNormdata`.
     */
    private readonly normdataMelding = new Melding<Normdatamelding>();

    /**
     * Er is iets met een rekenronde gebeurd; zie `bijNormronde`.
     */
    private readonly rondeMelding = new Melding<Normrondemelding>();

    /**
     * De fouttelling is bijgewerkt; zie `bijFouttelling`.
     */
    private readonly fouttellingMelding = new Melding();

    /**
     * De actieve taal. `TranslateService.instant` is niet reactief, dus het foutoverzicht hangt
     * hieraan om bij een taalwissel opnieuw te vertalen.
     */
    private readonly taal = signal(this.translateService.currentLang);

    /**
     * De telling per normtype (vers object per herberekening, zodat het signal bumpt).
     */
    private readonly _telling = signal<NormTypeTelling>({});

    /**
     * De namentelling.
     */
    private readonly _naamtelling = signal<NaamTelling>({norm: 0, aantal: 0});

    /**
     * De interpretaties uit de worker (of van de leider, via de samenwerk-baas), aflopend op score.
     */
    private readonly _norminterpretaties = signal<NormmatchOutput>([]);

    /**
     * De beste interpretatie (de eerste), of null.
     */
    private readonly _normmatch = signal<NormmatchInterpretatie | null>(null);

    /**
     * De foutsleutels die bij de laatst binnengekomen uitslag nieuw zijn. Bewust een verse `Set` per
     * uitslag, ook als hij leeg is: de feedbackbalk hangt aan de identiteit van dit signal en meldt
     * dus één keer per uitslag. Een boolean zou bij twee uitslagen mét nieuws achter elkaar geen
     * flank geven en de tweede melding dus overslaan.
     */
    private readonly _nieuweFoutsleutels = signal<Set<string>>(new Set());

    readonly telling = this._telling.asReadonly();
    readonly naamtelling = this._naamtelling.asReadonly();
    readonly norminterpretaties = this._norminterpretaties.asReadonly();
    readonly normmatch = this._normmatch.asReadonly();
    readonly nieuweFoutsleutels = this._nieuweFoutsleutels.asReadonly();

    /**
     * Het totale aantal normfouten over alle elementen in het foutmodel: de teller van het
     * vraagteken in de feedbackbalk (golf f1). Bewust een andere grootheid dan `telling`, dat per
     * normtype het aantal élementen mét fouten geeft voor de normbalk. De gate op `norm` volgt de
     * ng1-versie (`numNormfouten`), die zonder normmodel 0 teruggeeft.
     */
    readonly aantalFouten = computed(() => {
        if (!this.dlModel.norm) {
            return 0;
        }
        return Object.values(this.normmatch()?.foutmodel ?? {}).reduce((som, foutdata) => som + (foutdata.fouten?.length ?? 0), 0);
    });

    /**
     * Of er op dit moment normfouten zijn.
     */
    readonly heeftFouten = computed(() => this.aantalFouten() > 0);

    /**
     * Het normfout-overzicht: per vertaalde foutbeschrijving één regel met de elementen die hem
     * opleveren. Vervangt de ng1-`updateFoutweergave` en de luie verversing eromheen; de
     * groepering op de vertaalde tekst is bewust ongewijzigd, want regels die hetzelfde lezen
     * horen één knopje in de ballon te worden.
     */
    readonly foutoverzicht: Signal<NormFoutregel[]> = computed(() => {
        this.taal(); //de teksten zijn vertaald, dus een taalwissel bouwt het overzicht opnieuw op
        const foutmodel = this._normmatch()?.foutmodel;
        if (!this.dlModel.norm || !foutmodel) {
            return [];
        }
        const overzicht: NormFoutregel[] = [];
        for (const foutdata of Object.values(foutmodel)) {
            if (!foutdata.fouten.length) {
                continue;
            }
            const vertaalargumenten = this.vertaalargumentenVoor(foutdata.el);
            for (const foutcode of foutdata.fouten) {
                this.voegFoutToe(overzicht, foutdata.el, foutcode, vertaalargumenten);
            }
        }
        return overzicht;
    });

    /**
     * De lopende worker, of null.
     */
    private worker: Worker | null = null;

    /**
     * Of er op dit moment een normworker rekent. De opslagketen leest dit als voorwaarde voor rust:
     * zolang de worker loopt, bestaat de normstand van de huidige modelstand nog niet.
     */
    get rekent(): boolean {
        return this.worker !== null;
    }

    /**
     * Het overzicht waarop de meldingen-cache gebouwd is; wijzigt dat, dan gaat de cache om.
     */
    private meldingenBron: NormFoutregel[] = [];

    /**
     * De meldingen per node-id, op het huidige overzicht.
     */
    private meldingenCache = new Map<string, NormfeedbackMelding[]>();

    /**
     * De meldingen van het vórige overzicht. Levert de bestaande arrayreferentie terug zolang de
     * inhoud gelijk bleef, zodat een herbouwd overzicht met dezelfde fouten voor de OnPush-iconen
     * geen wijziging is (anders springt de ballon bij elke normronde terug naar melding 1).
     */
    private vorigeMeldingen = new Map<string, NormfeedbackMelding[]>();

    /**
     * De nieuws-set waarop de meldingen-cache gebouwd is; wijzigt die, dan gaat de cache om.
     */
    private meldingenNieuws = new Set<string>();

    /**
     * De fouten van de vorige uitslag, als `<elementId>|<foutcode>`; `null` zolang er voor dit model
     * nog geen uitslag geweest is. Tegen `null` is niets nieuw — dat is het antwoord op het laden
     * van een model, en het is een datavraag in plaats van een klok.
     *
     * Wordt bijgewerkt door élke uitslag die binnenkomt en alleen door die: de ijklijn is wat de
     * gebruiker daadwerkelijk gezien heeft, en een worker die onderweg gedood wordt heeft niets
     * laten zien.
     */
    private vorigeFoutsleutels: Set<string> | null = null;

    /**
     * De modelstand waarop we het laatst gerekend hebben. Een herberekening op diezelfde stand kan
     * per definitie niets nieuws opleveren — de normmatch is een montecarlo-zoektocht, dus twee
     * runs op hetzelfde model geven niet gegarandeerd dezelfde interpretatie — en wordt daarom
     * overgeslagen. Dat scheelt bovendien een tweede worker na een undo, en de opslaanknop, het
     * openen van de simulatie en de feedbacktoggles laten de norm nu met rust.
     */
    private laatsteStandsleutel: string | null = null;

    /**
     * Of er over de stand die er staat niets bekend is. Alleen een replaystap zonder normblok zet dit
     * aan: dan zijn de cijfers niet nul maar onbekend, en dat is iets anders om te tonen.
     */
    private normstandOnbekend = false;

    /**
     * Door de `OpslagService` geregistreerde callback voor de `help`-actielogregel; die kent het
     * model-id en de actielogbuffer.
     */
    private logHelpCallback: ((payload: HelpLogPayload) => void) | null = null;

    constructor() {
        this.translateService.onLangChange
            .pipe(takeUntilDestroyed())
            .subscribe(gebeurtenis => this.taal.set(gebeurtenis.lang));

        /*
         * De `help`-regel hoort bij een vers overzicht, niet bij de computed zelf: een berekening
         * hoort geen bijwerking te hebben. Hij trackt het overzicht (dus inclusief de taal), zodat
         * de index-ruimte waarin een latere `foutselect` schrijft altijd die van de laatst gelogde
         * `help`-regel is. Een volger logt niet: die krijgt dezelfde norminterpretatie van de
         * leider doorgestuurd en zou hem dubbel vastleggen.
         *
         * De readonly-guard staat hier expliciet en niet verstopt in `directlogAction` (werkregel
         * golf 3c). Hij leest bewust `fullReadonly` en níet `inReplay`: die vlag gaat aan in stap 1
         * van het ingaan van een replay en de blokkade pas in stap 4, en juist in dat venster komt de
         * laatste legitieme normuitslag binnen. Met een `inReplay`-guard zouden diens `errormodel`-
         * en `errorcount`-regels wél gelogd worden en de bijbehorende `help`-regel niet, en dan wijst
         * de eerstvolgende `foutselect` naar een indexruimte die nooit is vastgelegd.
         */
        effect(() => {
            const overzicht = this.foutoverzicht();
            if (!overzicht.length || untracked(this.samenwerking.isVolger)
                || untracked(() => this.dlModel.fullReadonly)) {
                return;
            }
            this.logHelpCallback?.({
                errors: overzicht.map(regel => ({
                    tekst: regel.tekst,
                    fout: regel.fout,
                    gebruikt: regel.gebruikt,
                    elementen: regel.elementen.map(element => element.el)
                }))
            });
        });
    }

    /**
     * Generatieteller: resultaten van een oudere generatie (na een reset of een nieuwe run)
     * worden genegeerd.
     */
    private generatie = 0;


    /**
     * De stand van het model is gewijzigd: reken de telling opnieuw en start zo nodig een verse
     * normmatch. Dit is de trigger die tot golf a3 de ng1-`wijzigHooks()` was; de opslagketen roept
     * hem aan met de modeldata van dat moment, want die bezit de stand.
     *
     * Een volger rekent niet: hij krijgt de uitkomst van zijn leider. En zonder norm valt er
     * sowieso niets te matchen.
     * @param werkModel de `model`-helft van het opslagformaat
     * @param [aanleiding] waarom de stand veranderde; bij een herstel telt de uitslag niet als nieuw
     */
    verwerkStand(werkModel: WerkModelData, aanleiding?: Tekenaanleiding): void {
        //de sleutel gaat vooraf aan de guards, want elke afloop hoort gemeld te worden — ook "er
        //gebeurt hier niets". Voor de opslagketen is dat het teken dat er niets meer komt
        const standsleutel = this.standsleutelVoor(werkModel);
        if (this.dlModel.fullReadonly) {
            this.log.debug('verwerkStand: geskipt wegens fullReadonly');
            this.meldRonde('geenronde', standsleutel, undefined, 'fullReadonly');
            return;
        }
        if (this.samenwerking.isVolger()) {
            this.log.debug('verwerkStand: we wachten op de leider');
            this.meldRonde('geenronde', standsleutel, undefined, 'volger');
            return;
        }
        if (standsleutel === this.laatsteStandsleutel) {
            //dezelfde modelstand: de telling staat al, en een herberekening kan per definitie niets
            //nieuws opleveren. Dit is de opslagronde die op een undo volgt, de opslaanknop, het
            //openen van de simulatie, een feedbacktoggle en een beantwoorde bouwsuggestie
            this.log.debug('verwerkStand: dezelfde modelstand, niets te herrekenen');
            this.meldRonde('geenronde', standsleutel, undefined, 'ongewijzigde stand');
            return;
        }
        this.laatsteStandsleutel = standsleutel;
        //het model wijzigt: het nieuws van de vorige ronde is voorbij. De volgers krijgen datzelfde
        //signaal in de telling-melding hieronder, zodat beide kanten op hetzelfde moment op nul staan
        this._nieuweFoutsleutels.set(new Set());
        this.berekenTelling(werkModel);
        const norm = this.dlModel.norm;
        if (norm && norm.teller) {
            //de telling is vers: de leider stuurt hem naar zijn volgers
            this.normdataMelding.meld({
                interpretaties: this._norminterpretaties(),
                gewijzigdFoutmodel: null,
                nieuw: []
            });
        }
        //een normmatch maken we alleen als het project er iets mee doet
        if (norm && (this.projectVlaggen.normFoutfeedback() || this.projectVlaggen.normFoutfeedbackHighlight())) {
            this.maakNormmatch(werkModel.elements, herkomstVanAanleiding(aanleiding));
        } else {
            //de telling is wél vers; alleen een foutmodel komt er niet
            this.meldRonde('geenronde', standsleutel, undefined, 'geen normmatch in dit project');
        }
        this.log.debug('verwerkStand', norm && norm.teller, this._telling());
    }

    /**
     * Meldt wat er met een rekenronde gebeurt; zie `Normrondemelding`.
     */
    private meldRonde(stand: Normrondestand, standsleutel: string | null,
                      generatie?: number, reden?: string): void {
        this.rondeMelding.meld({stand, standsleutel, generatie, reden});
    }

    /**
     * Abonneert op de rekenrondes. De opslagketen leest er het rustsignaal voor zijn snapshots uit.
     */
    bijNormronde(luisteraar: (melding: Normrondemelding) => void): void {
        this.rondeMelding.abonneer(luisteraar);
    }

    /**
     * De sleutel waarmee twee modelstanden vergeleken worden. Het normmodel hoort erbij: wisselt dat
     * zonder dat het werkmodel verandert, dan is het wél een nieuwe vraag.
     */
    private standsleutelVoor(werkModel: WerkModelData): string {
        const norm = this.dlModel.norm;
        return `${JSON.stringify(werkModel)}|${norm ? (norm.from?.id ?? 'norm') : 'geen'}`;
    }

    /**
     * Laat de worker een optimale interpretatie maken, gegeven het normmodel en het huidige
     * werkmodel. Zonder norm valt er niets te matchen: een lopende worker mag dan geen oude match
     * meer terugbrengen, en wat er stond gaat weg.
     * @param werkElements de elementen van het werkmodel
     * @param [herkomst] waar de uitslag straks vandaan komt. De standaard is `teruggehaald`, want de
     *   losse aanroeper — een verse leider zonder normmatch, via `OpslagService.maakNormmatch` —
     *   vraagt om een herberekening zonder modelwijziging, en die mag per definitie niets nieuws
     *   opleveren
     */
    maakNormmatch(werkElements: NormmatchElementen, herkomst: Feedbackherkomst = 'teruggehaald'): void {
        const norm = this.dlModel.norm;
        if (!norm) {
            this.log.debug('Geen norm - niets te matchen');
            this.stopWorker();
            this.verwerkNormdata(null, null, herkomst);
            return;
        }
        if (this.samenwerking.isVolger()) {
            this.log.debug('maakNormmatch: we wachten op de leider');
            return;
        }
        if (!(norm.from && norm.from.content)) {
            //een oud model dat op een normmodel gebouwd is maar de inhoud ervan niet bewaarde
            this.log.error('Oud model obv normmodel - kan niet geïnterpreteerd worden', norm);
            this.meldRonde('geenronde', this.laatsteStandsleutel, undefined, 'norminhoud ontbreekt');
            return;
        }
        this.startWorker(werkElements, herkomst);
    }

    /**
     * Verwerkt verse norminterpretaties: van onze eigen worker, of van de leider doorgekregen. Zet
     * de state, werkt de fouttelling bij en meldt allebei — het loggen en het verzenden dat daarop
     * volgt is niet van de norm maar van de opslag en de samenwerking.
     * @param interpretaties de nieuwe interpretaties, of `null` om ze te wissen
     * @param [tellingen] een meegestuurde telling (van de leider)
     * @param [herkomst] waar deze uitslag vandaan komt; bepaalt wat "nieuw" erin betekent
     * @param [nieuwVanLeider] bij herkomst `vanLeider`: de foutsleutels die de leider als nieuw meldt
     * @param [generatie] de workergeneratie waar deze uitslag uit komt. Alleen een eigen, lopende
     *   ronde geeft hem mee; een uitslag van buiten — de leider, een replaystap, het geen-norm-pad —
     *   laat hem weg en maakt daarmee de standsleutel ongeldig
     */
    verwerkNormdata(
        interpretaties: NormmatchOutput | null,
        tellingen?: {telling: NormTypeTelling; naamtelling: NaamTelling} | null,
        herkomst: Feedbackherkomst = 'berekend',
        nieuwVanLeider?: string[],
        generatie?: number
    ): void {
        //er komt een uitslag binnen, dus er is weer iets bekend over deze stand
        this.normstandOnbekend = false;
        //het foutmodel vóór de overdracht, zodat we kunnen zien of er iets veranderd is
        const oudFoutmodel = this._normmatch() ? this.foutenIn(this._normmatch()!.foutmodel) : [];
        const nieuwFoutmodel = interpretaties?.length ? this.foutenIn(interpretaties[0].foutmodel) : null;

        //Wat er nieuw is bepalen we hier, op het moment dat de data binnenkomt: hier is zeker waar
        //hij vandaan komt. Verderop in de keten is dat niet meer te weten zonder een vlag, en zo'n
        //vlag moet dan een debounce overleven.
        const verse = this.foutsleutelsIn(interpretaties);
        const nieuw = this.bepaalNieuweFoutsleutels(verse, herkomst, nieuwVanLeider);
        this.vorigeFoutsleutels = verse;
        this._nieuweFoutsleutels.set(nieuw);

        this.zetNormdata(interpretaties, tellingen);

        //een standsleutel hoort bij een ronde die wij zelf gestart zijn. Komt deze uitslag van
        //buiten, dan zegt hij niets meer over wat er op het scherm staat, en moet een volgende ronde
        //over diezelfde stand gewoon draaien in plaats van overgeslagen te worden
        if (generatie === undefined) {
            this.laatsteStandsleutel = null;
        }

        this.normdataMelding.meld({
            interpretaties: this._norminterpretaties(),
            //een teruggehaalde uitslag is geen nieuws en levert dus geen `errormodel`-regel op
            gewijzigdFoutmodel: (herkomst !== 'teruggehaald' && nieuwFoutmodel
                && !NormService.zelfdeFoutmodellen(oudFoutmodel, nieuwFoutmodel)) ? nieuwFoutmodel : null,
            nieuw: [...nieuw]
        });
        this.updateFouttelling();
        this.fouttellingMelding.meld();
        //als laatste, want de opslagketen kan hierop een snapshot maken met de normstand uit deze
        //service, en die moet dus compleet zijn — inclusief de foutaantallen van `updateFouttelling`
        if (generatie !== undefined) {
            this.meldRonde('uitslag', this.laatsteStandsleutel, generatie);
        }
    }

    /**
     * Zet de normstand van een replaystap neer: het foutmodel en de tellingen zoals ze bij díe stap
     * hoorden. Dat gaat langs de gewone weg, `verwerkNormdata` met herkomst `teruggehaald`, en
     * daarmee is meteen geregeld dat er niets pulseert — sinds golf a4 stap 7 bepaalt die methode het
     * nieuws bij binnenkomst, en teruggehaald levert per definitie een lege set op.
     *
     * Waarom deze ingang dan bestaat: de snapshot draagt per element alleen `el` en `fouten`, terwijl
     * een `NormmatchInterpretatie` ook `score`, `id` en drie mappings eist — de normsnap
     * (`DlModel.snapNaarNorm`) leest die echt. Hier wordt daar een complete vorm van gemaakt, met
     * lege mappings.
     *
     * Zonder blok (een oude snapshot, of een stap waarvan de rekenronde het niet haalde) is er over
     * deze stap niets bekend. Dan gaat alles leeg én markeren we dat als zodanig, zodat de normbalk
     * dimt in plaats van de cijfers van een andere stand te tonen.
     * @param blok de normstand uit de snapshot, of `null` als die er niet is
     */
    zetReplaystand(blok: NormSnapshotblok | null): void {
        if (!blok) {
            this.verwerkNormdata(null, {telling: {}, naamtelling: {norm: 0, aantal: 0}}, 'teruggehaald');
            this.normstandOnbekend = true;
            this.stuurNormtellingNaarDlModel();
            return;
        }
        const naamtelling: NaamTelling = {norm: blok.naamtelling.verwacht, aantal: blok.naamtelling.aantal};
        this.verwerkNormdata(this.interpretatieUit(blok), {telling: blok.telling, naamtelling}, 'teruggehaald');
    }

    /**
     * Maakt van het uitgeklede foutmodel van een snapshot een interpretatie zoals de weergave hem
     * verwacht. De mappings blijven leeg: die zijn niet bewaard, en de weergave leest ze niet — alleen
     * de normsnap doet dat, en die staat tijdens een replay uit.
     */
    private interpretatieUit(blok: NormSnapshotblok): NormmatchOutput {
        if (!blok.foutmodel) {
            return [];
        }
        const foutmodel: NormmatchFoutmodel = {};
        for (const regel of blok.foutmodel) {
            foutmodel[regel.el.id] = {el: regel.el, fouten: regel.fouten};
        }
        return [{
            score: 0,
            id: null,
            mappings: [],
            mappings_op_werk: {},
            mappings_op_norm: {},
            foutmodel
        }];
    }

    /**
     * De normstand zoals hij in een snapshot meegaat; zie `NormSnapshotblok`.
     */
    snapshotNormstand(): NormSnapshotblok {
        const teller = this.dlModel.norm ? this.dlModel.norm.teller : null;
        const telling: NormSnapshotblok['telling'] = {};
        for (const [normtype, tel] of Object.entries(this._telling())) {
            telling[normtype] = {verwacht: teller?.[normtype] ?? 0, aantal: tel.aantal, fouten: tel.fouten};
        }
        const foutmodel = this._normmatch()?.foutmodel;
        return {
            //alleen `el` en `fouten`: de mapping erachter is tweederde van de bytes en wordt door de
            //weergave nergens gelezen (in de gelógde `errormodel`-regels blijft hij wél nodig). En van `el`
            //niet de bouwsuggestiedata (`bs_data`, `bs`) die het element bij de rekenronde droeg: de norm
            //leest die niet, en hij loopt achter op de run, waardoor dezelfde stand een andere snapshot werd
            foutmodel: foutmodel ? this.foutenIn(foutmodel).map(regel => {
                const {bs_data: _ballonstand, bs: _regelstatus, ...el} = regel.el;
                return {el, fouten: regel.fouten};
            }) : null,
            telling,
            naamtelling: {verwacht: this._naamtelling().norm, aantal: this._naamtelling().aantal}
        };
    }

    /**
     * Legt de normstand van dit moment vast om hem later terug te zetten: de interpretaties, de
     * tellingen, de ijklijn voor "nieuw" en de modelstand waar de uitslag bij hoort. De replay doet dit
     * bij het binnengaan, want elke replaystap zet zijn eigen normstand neer.
     *
     * De modelstand gaat alleen mee als er een uitslag ligt en er niets meer rekent: alleen dan hoort
     * wat er ligt ook echt bij die stand.
     * @param werkModel de `model`-helft van de stand van dit moment
     */
    bewaarStand(werkModel: WerkModelData): BewaardeNormstand {
        return {
            interpretaties: this._norminterpretaties(),
            telling: {telling: this._telling(), naamtelling: this._naamtelling()},
            vorigeFoutsleutels: this.vorigeFoutsleutels,
            standsleutel: this.rekent || this.laatsteStandsleutel === null ? null : this.standsleutelVoor(werkModel)
        };
    }

    /**
     * Zet een bewaarde normstand terug als teruggehaalde uitslag: er pulseert niets en er wordt niets
     * gelogd. Met de modelstand erbij slaat de eerstvolgende ronde over diezelfde stand over, en komt er
     * dus geen nieuwe interpretatie. Dat is de bedoeling: de montecarlo-zoektocht kan op hetzelfde model
     * een fout op een andere node leggen, en dan gaf het verlaten van een replay een ander beeld dan het
     * binnengaan.
     * @param bewaard wat `bewaarStand` opleverde
     */
    herstelStand(bewaard: BewaardeNormstand): void {
        this.verwerkNormdata(bewaard.interpretaties, bewaard.telling, 'teruggehaald');
        this.vorigeFoutsleutels = bewaard.vorigeFoutsleutels;
        this.laatsteStandsleutel = bewaard.standsleutel;
    }

    /**
     * De foutsleutels (`<elementId>|<foutcode>`) in een binnengekomen uitslag.
     */
    private foutsleutelsIn(interpretaties: NormmatchOutput | null): Set<string> {
        const sleutels = new Set<string>();
        for (const foutdata of Object.values(interpretaties?.[0]?.foutmodel ?? {})) {
            for (const foutcode of (foutdata.fouten ?? [])) {
                sleutels.add(`${foutdata.el.id}|${foutcode}`);
            }
        }
        return sleutels;
    }

    /**
     * Welke fouten er bij deze uitslag bíj komen. Wie de uitslag van een ander overneemt neemt ook
     * diens antwoord over; een teruggehaalde stand levert per definitie niets op; en zonder
     * voorganger — een net geladen model — valt er niets nieuw te noemen.
     */
    private bepaalNieuweFoutsleutels(verse: Set<string>, herkomst: Feedbackherkomst,
                                     nieuwVanLeider?: string[]): Set<string> {
        switch (herkomst) {
            case 'vanLeider':
                return new Set(nieuwVanLeider ?? []);
            case 'teruggehaald':
                return new Set();
            default:
                return this.vorigeFoutsleutels
                    ? new Set([...verse].filter(sleutel => !this.vorigeFoutsleutels!.has(sleutel)))
                    : new Set();
        }
    }

    /**
     * Er zijn verse norminterpretaties (of een verse telling). De luisteraar bepaalt zelf wat hij
     * ermee doet: de opslagketen logt een gewijzigd foutmodel, de samenwerking stuurt de
     * interpretaties naar de volgers.
     */
    bijNormdata(luisteraar: (melding: Normdatamelding) => void): void {
        this.normdataMelding.abonneer(luisteraar);
    }

    /**
     * De fouttelling is bijgewerkt. De opslagketen logt hem en stuurt hem naar het backend.
     */
    bijFouttelling(luisteraar: () => void): void {
        this.fouttellingMelding.abonneer(luisteraar);
    }

    /**
     * De elementen mét fouten uit een foutmodel; dat is wat er in de actielog terechtkomt.
     */
    private foutenIn(foutmodel: NormmatchFoutmodel | undefined): NormFoutmodelregel[] {
        return Object.values(foutmodel ?? {}).filter(regel => regel.fouten?.length);
    }

    /**
     * Of twee gefilterde foutmodellen dezelfde fouten beschrijven. Vergelijken op de json-vorm: dat
     * is precies wat er gelogd wordt, en het is de vorm die de ng1-`angular.equals` hier ook zag.
     */
    private static zelfdeFoutmodellen(links: NormFoutmodelregel[], rechts: NormFoutmodelregel[]): boolean {
        return JSON.stringify(links) === JSON.stringify(rechts);
    }

    /**
     * Registreert de callback waarmee de `OpslagService` de `help`-regel in het actielog zet.
     */
    registreerLogHelp(callback: (payload: HelpLogPayload) => void): void {
        this.logHelpCallback = callback;
    }

    /**
     * Zoekt de plaats van een element-met-foutcode in het huidige overzicht: de regel die dat
     * element voor die fout bevat, plus de positie daarbinnen. `null` als de fout er niet (meer)
     * is — bij een selectie van een samenwerkpartner kan de normmatch van de twee clients een tik
     * uiteenlopen, en dan is niets doen beter dan het verkeerde element highlighten.
     */
    zoekFoutregel(elementId: string, foutcode: string): FoutPositie | null {
        const overzicht = this.foutoverzicht();
        for (const [foutindex, regel] of overzicht.entries()) {
            const elementindex = regel.elementen.findIndex(element => element.el.id === elementId && element.foutcode === foutcode);
            if (elementindex >= 0) {
                return {foutindex, elementindex};
            }
        }
        return null;
    }

    /**
     * De normfeedback-meldingen voor één node: per foutregel waarin de node voorkomt één melding.
     * De arrayreferentie blijft stabiel zolang de inhoud gelijk is — `dl-normfeedback` reageert op
     * `ngOnChanges` van zijn `meldingen`-input en zou anders bij elke normronde zijn ballon
     * terugzetten.
     */
    getMeldingenVoorNode(node: NodeSingular): NormfeedbackMelding[] {
        const overzicht = this.verversAfgeleiden();
        const nodeId = node.id();
        const gecached = this.meldingenCache.get(nodeId);
        if (gecached) {
            return gecached;
        }
        const nieuw = this._nieuweFoutsleutels();
        const meldingen: NormfeedbackMelding[] = [];
        for (const regel of overzicht) {
            const eigen = regel.elementen.filter(element => element.el.id === nodeId);
            if (eigen.length) {
                meldingen.push({
                    type: regel.fout,
                    tekst: regel.tekst,
                    isNieuw: eigen.some(element => nieuw.has(`${element.el.id}|${element.foutcode}`))
                });
            }
        }
        const vorige = this.vorigeMeldingen.get(nodeId);
        const resultaat = vorige && NormService.zelfdeMeldingen(vorige, meldingen) ? vorige : meldingen;
        this.meldingenCache.set(nodeId, resultaat);
        return resultaat;
    }

    /**
     * Herbereken de normtelling en de naamtelling over het aangeleverde werkmodel, tegen het
     * `norm`-document in `DlModel`. Port van het telling-deel van ng1's `updateTelling`; de
     * samenwerkings- en normmatch-beslissingen eromheen neemt `verwerkStand`.
     */
    berekenTelling(werkModel: WerkModelData): void {
        const telling: NormTypeTelling = {};
        const naamtelling: NaamTelling = {norm: 0, aantal: 0};
        const norm = this.dlModel.norm;
        if (norm && norm.teller) {
            const normtypes = Object.keys(norm.teller);
            for (const nt of normtypes) {
                telling[nt] = {
                    verwacht: norm.teller[nt],
                    aantal: 0,
                    fouten: null
                };
            }

            for (const [modeltype, modelElements] of Object.entries(werkModel.types)) {
                //op zoek naar het juiste element in de telling
                //vanwege de verschillende modelleerniveaus zoeken we door en kan een element bij meerdere meetellen
                for (const normtype of normtypes) {
                    if (elements.isSubtypeOf(modeltype, normtype)) {
                        telling[normtype].aantal += modelElements.length;
                        //geen break: kan bij meerdere horen (modelleerniveaus)
                    }
                }
            }

            //de namen:
            for (const naam of Object.keys(norm.namen)) {
                naamtelling.norm += norm.namen[naam]; //aantal verwacht
                //elementen met deze naam tellen mee met de telling
                //maar tot maximaal het normaantal, want dubbel gebruik telt niet mee
                let teller = 0;
                for (const element of Object.values(werkModel.elements)) {
                    let telnaam = element.nodename;
                    //speciaal
                    if (element.type === 'quantity_space_point' && element.isZero) {
                        telnaam = '___qszero';
                    }
                    if (telnaam === naam) {
                        teller++;
                    }
                }
                //maximaal het normaantal
                naamtelling.aantal += Math.min(teller, norm.namen[naam]);
            }
        }
        this._telling.set(telling);
        this._naamtelling.set(naamtelling);
        this.log.debug(`berekenTelling`, norm && norm.teller, telling);
        this.stuurNormtellingNaarDlModel();
    }

    /**
     * Laat de worker een optimale interpretatie maken van het aangeleverde werkmodel op het
     * `norm`-document in `DlModel`. Een eventueel lopende worker wordt getermineerd (de
     * bestaande normmatch blijft staan totdat de nieuwe klaar is — geen tijdelijke 0-telling).
     * Het resultaat komt asynchroon binnen via de geregistreerde callback, tenzij er
     * ondertussen een nieuwe generatie is gestart.
     *
     * De herkomst reist mee in de closure van dít verzoek. Daarmee kan een nieuwer verzoek de
     * herkomst van een ouder verzoek niet lezen, en is er geen vlag nodig die de looptijd van de
     * worker moet overleven.
     * @param werkElements de elementen van het werkmodel
     * @param [herkomst] waar de uitslag van deze run vandaan komt
     */
    startWorker(werkElements: NormmatchElementen, herkomst: Feedbackherkomst = 'berekend'): void {
        //een lopende worker gaat eraf. Dat is óók een afbreking, al gaat hij niet langs `stopWorker`:
        //een nieuwe modelstand en een promotie tot leider doen dit
        const afgebroken = this.worker ? this.generatie : null;
        if (this.worker) {
            this.log.debug(`Kill runnende normmatch-worker`);
            this.worker.terminate();
            this.worker = null;
        }

        const norm = this.dlModel.norm;
        if (!norm) {
            if (afgebroken !== null) {
                this.meldRonde('afgebroken', this.laatsteStandsleutel, afgebroken, 'geen norm');
            }
            return; //`maakNormmatch` vangt dit al af; hier alleen een vangnet
        }

        this.generatie++;
        const workergeneratie = this.generatie; //de generatie waarmee we werken
        //bewust een relatief pad en niet de `dynalearn-common/*`-mapping: de bundler resolvet deze
        //URL zelf, buiten TypeScript om, en kent de tsconfig-paden dus niet
        const worker = new Worker(new URL('../../../dynalearn-common/lib/model.normmatch.worker', import.meta.url), {type: 'module'});
        this.worker = worker;
        //een worker die crasht stuurt geen bericht. Zonder deze afhandeling bleef hij als lopend
        //staan, en wachtte de opslagketen op een rust die nooit komt
        worker.onerror = (ev: ErrorEvent) => {
            this.log.error('normmatch-worker gecrasht', ev.message);
            worker.terminate();
            if (this.worker !== worker) {
                return; //al vervangen door een nieuwere ronde
            }
            this.worker = null;
            this.laatsteStandsleutel = null; //nooit geland: een volgende ronde over deze stand moet draaien
            this.meldRonde('afgebroken', null, workergeneratie, 'workerfout');
        };
        worker.onmessage = (ev: MessageEvent<NormmatchOutput>) => {
            this.log.debug('normmatch-worker return generatie', workergeneratie, this.generatie);
            if (workergeneratie !== this.generatie) {
                this.log.debug(`Normmatch-worker klaar maar op oude generatie. Stop.`);
                return;
            }
            worker.terminate(); //klaar
            if (this.worker === worker) {
                this.worker = null; //en weg
            }
            //twee afvoeren waar de uitslag stil verdwijnt. Ze melden wél een afbreking, anders
            //wachtte de opslagketen op een uitslag die niet komt
            if (this.samenwerking.isVolger()) {
                this.log.debug('normmatch klaar, maar we volgen inmiddels');
                this.meldRonde('afgebroken', this.laatsteStandsleutel, workergeneratie, 'volger geworden');
                return; //de leider bepaalt vanaf nu wat de norm zegt
            }
            //alleen als de replay al geblokkeerd staat: pas dan kan er een teruggebladerde stand op het
            //canvas staan. In het venster daarvóór (de vlag staat, de blokkade nog niet) is dit de
            //laatste legitieme uitslag, en de snapshot van de laatste actie wacht erop — zie ook de
            //guard op het `help`-effect
            if (this.dlModel.inReplay() && this.dlModel.fullReadonly) {
                this.log.debug('normmatch klaar, maar er loopt een replay');
                this.meldRonde('afgebroken', this.laatsteStandsleutel, workergeneratie, 'replay');
                return; //wat er op het canvas staat is een teruggebladerde stand, niet deze
            }
            //de generatie gaat mee: dit is een eigen ronde, dus de standsleutel blijft geldig
            this.verwerkNormdata(ev.data, null, herkomst, undefined, workergeneratie);
        };

        this.log.debug(`run worker generatie ${workergeneratie}`);
        //de afbreking van de vorige ronde pas nu, met de nieuwe al lopend: een afbreking telt als rust
        //met wat er ligt, behalve als er meteen een nieuwe ronde start
        if (afgebroken !== null) {
            this.meldRonde('afgebroken', this.laatsteStandsleutel, afgebroken, 'nieuwe ronde');
        }
        this.meldRonde('gestart', this.laatsteStandsleutel, workergeneratie);
        const input: NormmatchInput = {
            norm: norm.from.content.elements as NormmatchElementen,
            werk: werkElements
        };
        worker.postMessage(input);
    }

    /**
     * Termineert een eventueel lopende worker zonder de uitslag te raken. Voor het geen-norm-pad van
     * `maakNormmatch`, en voor het ingaan van een replay: er mag geen oude match meer
     * terugkomen.
     *
     * Twee dingen gaan mee. De generatie loopt op, zodat een `onmessage` die al in de taakwachtrij
     * staat alsnog op de generatiecontrole afvalt. En de standsleutel gaat leeg: hij hoorde bij een
     * ronde die nooit geland is, dus een volgende ronde over dezelfde stand hoort wél te draaien.
     */
    stopWorker(): void {
        this.meldRonde('afgebroken', this.laatsteStandsleutel, this.generatie, 'gestopt');
        this.generatie++;
        this.laatsteStandsleutel = null;
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
    }

    /**
     * Zet nieuwe norminterpretaties en/of tellingen: gegenereerd door de worker of van de leider
     * doorgekregen. Puur de state; de plumbing eromheen (foutmodel-vergelijking voor het log, de
     * fouttelling-update en de meldingen) doet de aanroeper, `verwerkNormdata`.
     */
    zetNormdata(interpretaties: NormmatchOutput | null, tellingen?: {telling: NormTypeTelling; naamtelling: NaamTelling} | null): void {
        if (interpretaties && interpretaties.length) {
            this._norminterpretaties.set(interpretaties);
            this._normmatch.set(interpretaties[0]);
            this.log.debug('Nieuwe normmatch gezet', this._normmatch());
        } else {
            this._norminterpretaties.set([]);
            this._normmatch.set(null);
        }
        if (tellingen) {
            this._telling.set(tellingen.telling);
            this._naamtelling.set(tellingen.naamtelling);
        }
        this.stuurNormtellingNaarDlModel();
    }

    /**
     * Werk de foutaantallen in de telling bij op basis van de huidige normmatch. Port van het
     * rekenwerk van ng1's `updateFouttelling`; het loggen en versturen daarna doet de `OpslagService`
     * op `bijFouttelling`.
     */
    updateFouttelling(): void {
        const normmatch = this._normmatch();
        const telling: NormTypeTelling = {};
        for (const [nt, tel] of Object.entries(this._telling())) {
            telling[nt] = {
                verwacht: tel.verwacht,
                aantal: tel.aantal,
                fouten: normmatch ? Object.values(normmatch.foutmodel ?? {}).filter(f => f.fouten && f.fouten.length && f.el && elements.isSubtypeOf(f.el.type, nt)).length : null
            };
        }
        this._telling.set(telling);
        this.stuurNormtellingNaarDlModel();
    }

    /**
     * Debughulp voor de ontwikkelaar: logt hoe een element van het werkmodel op het normmodel gemapt is
     * — het werkelement, het normelement, de gevuurde regels en de score — en bij een relatie of zijn
     * uiteinden op dezelfde normelementen uitkomen. In AngularJS gebeurde dit bij een tik op een relatie
     * als de logger op debug stond; sinds golf 4d staat die aanroep in het modelcanvas uitgecommentarieerd
     * (besluit gebruiker, 22-09-2026) en blijft deze methode beschikbaar.
     * @param elementId het werkelement
     */
    logMapping(elementId: string): void {
        const normmatch = this._normmatch();
        if (!(this.dlModel.norm && normmatch)) {
            return;
        }
        this.log.info('**** MAPPING ****');
        const mapping = normmatch.mappings.find(m => m.werk?.id === elementId);
        if (!mapping) {
            this.log.info('Geen mapping');
            this.log.info('**********************************************');
            return;
        }
        this.log.info('Werkelement', mapping.werk);
        this.log.info('Match norm', mapping.norm);
        this.log.info('Score-regels', mapping.regels.join(', '));
        this.log.info(`Score: ${mapping.score}`);
        for (const kant of ['from', 'to'] as const) {
            if (mapping.werk[kant] && mapping.norm[kant]) {
                const uiteinde = normmatch.mappings.find(m => m.werk?.id === mapping.werk[kant]);
                this.log.info(`MAPPING ${kant.toUpperCase()}`, uiteinde);
                this.log.info(uiteinde?.norm.id === mapping.norm[kant] ? `${kant.toUpperCase()} KLOPT` : `${kant.toUpperCase()} FOUT`);
            }
        }
        this.log.info(mapping);
        this.log.info('**********************************************');
    }

    /**
     * Reset voor een nieuw/leeg model: oude workerresultaten zijn ongeldig, de state gaat leeg.
     * `DlModel.normtelling` wordt hier niet aangeraakt: dat doet `DlModel.reset()` zelf, in
     * dezelfde `OpslagService.reset()`.
     */
    reset(): void {
        this.meldRonde('afgebroken', this.laatsteStandsleutel, this.generatie, 'reset');
        this.generatie++; //oude normmatches zijn ongeldig
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        this._norminterpretaties.set([]);
        this._normmatch.set(null);
        this._telling.set({});
        this._naamtelling.set({norm: 0, aantal: 0});
        //ander model: de fouten van het vorige zeggen niets meer over wat er nieuw is. `null` en niet
        //een lege set — er is geen voorganger, en dan is de eerste uitslag per definitie geen nieuws
        this.vorigeFoutsleutels = null;
        this._nieuweFoutsleutels.set(new Set());
        this.laatsteStandsleutel = null;
        this.normstandOnbekend = false;
    }

    /**
     * Zet de actuele telling in `DlModel.normtelling`, waar de normbalk hem uitleest. Elke
     * aanroep zet een vers object, zodat het signal bumpt (recept golf e3).
     */
    private stuurNormtellingNaarDlModel(): void {
        this.dlModel.normtelling = {
            telling: this._telling(),
            naamtelling: this._naamtelling(),
            heeftMatch: !!this._normmatch(),
            onbekend: this.normstandOnbekend
        };
    }

    /**
     * Zorgt dat de meldingen-cache bij het actuele overzicht en de actuele nieuws-set hoort, en geeft
     * dat overzicht terug. Bij een verse opbouw schuift de cache door naar de vorige ronde, voor de
     * stabiele arrayreferenties. Het lezen van de signals hoort hier ook bij een cache-hit: dat
     * registreert de dependency van de aanroepende template.
     *
     * De nieuws-set telt mee als bron: gaat een melding van nieuw naar niet-nieuw zonder dat het
     * overzicht wijzigt, dan hoort de cache om — anders leest een icoon een `isNieuw` dat bij een
     * vorige ronde hoort.
     */
    private verversAfgeleiden(): NormFoutregel[] {
        const overzicht = this.foutoverzicht();
        const nieuws = this._nieuweFoutsleutels();
        if (overzicht !== this.meldingenBron || nieuws !== this.meldingenNieuws) {
            this.meldingenBron = overzicht;
            this.meldingenNieuws = nieuws;
            this.vorigeMeldingen = this.meldingenCache;
            this.meldingenCache = new Map();
        }
        return overzicht;
    }

    /**
     * Of twee meldingenlijsten dezelfde inhoud hebben; bepaalt of de vorige arrayreferentie
     * hergebruikt mag worden. `isNieuw` telt mee: een melding die niet langer nieuw is hoort een
     * verse referentie op te leveren, zodat het icoon zijn animatie-afweging opnieuw maakt.
     */
    private static zelfdeMeldingen(links: NormfeedbackMelding[], rechts: NormfeedbackMelding[]): boolean {
        return links.length === rechts.length &&
            links.every((melding, index) => melding.type === rechts[index].type
                && melding.tekst === rechts[index].tekst && melding.isNieuw === rechts[index].isNieuw);
    }

    /**
     * De argumenten waarmee een foutbeschrijving vertaald wordt: het element zelf, aangevuld met
     * een toonbare naam (bij een relatie `van ➔ naar`, anders "(geen naam)") en met de data van de
     * parent.
     */
    private vertaalargumentenVoor(el: NormmatchElement): Record<string, unknown> {
        const cy = this.modelcanvas.getCy();
        const argumenten: Record<string, unknown> = {...el};
        if (!el.nodename) {
            argumenten['nodename'] = elements.isRelation(el.type) ? this.relatienaam(el, cy) : '(geen naam)';
        }
        argumenten['nodename'] = specialenaam(el.isZero ? '___qszero' : String(argumenten['nodename']), this.translateService);
        if (el.parentId) {
            argumenten['parent'] = cy?.$id(el.parentId).data();
        }
        return argumenten;
    }

    /**
     * De toonbare naam van een naamloze relatie: de namen van zijn twee argumenten met een pijl
     * ertussen.
     */
    private relatienaam(el: NormmatchElement, cy: Core | null): string {
        const naamVan = (id: string | undefined): string => {
            const node = id ? cy?.$id(id) : null;
            return node?.nonempty() ? (node.data('nodename') || '?') : '?';
        };
        return `${naamVan(el.from)} ➔ ${naamVan(el.to)}`;
    }

    /**
     * Zoekt de vertaling voor één fout op een element en zet hem in het overzicht: bij de regel met
     * dezelfde tekst, of in een nieuwe regel. Zonder vertaling voor welke sleutel dan ook blijft de
     * fout buiten het overzicht, met een waarschuwing in de log.
     */
    private voegFoutToe(overzicht: NormFoutregel[], el: NormmatchElement, foutcode: string, vertaalargumenten: Record<string, unknown>): void {
        for (const foutsleutel of this.foutsleutelsVoor(el, foutcode)) {
            const vertaalsleutel = `NORMFOUTEN.${foutsleutel}`;
            const vertaling = this.translateService.instant(vertaalsleutel, vertaalargumenten);
            if (vertaling === vertaalsleutel) {
                continue; //geen vertaling onder deze sleutel; de volgende proberen
            }
            let regel = overzicht.find(kandidaat => kandidaat.tekst === vertaling);
            if (!regel) {
                //`fout` is de meest specifieke sleutel van het element dat de regel aanmaakte, ook
                //als de vertaling via een generiekere sleutel gevonden werd (vorm van het actielog)
                regel = {tekst: vertaling, fout: `${el.type || 'unk'}_${foutcode}`, gebruikt: vertaalsleutel, elementen: []};
                overzicht.push(regel);
            }
            regel.elementen.push({el, foutcode});
            return;
        }
        this.log.warn(`Geen foutbeschrijving voor ${foutcode} bij`, el);
    }

    /**
     * De vertaalsleutels voor een fout op een element, in aflopende specificiteit: het eigen type,
     * de supertypes, `any` voor de fout, `any` voor het type (weer met de supertypes) en tenslotte
     * `any_any`.
     */
    private foutsleutelsVoor(el: NormmatchElement, foutcode: string): string[] {
        const type = el.type || 'unk';
        const ups = elements.alleUps(el.type);
        return [
            `${type}_${foutcode}`,
            ...ups.map(up => `${up}_${foutcode}`),
            `any_${foutcode}`,
            `${type}_any`,
            ...ups.map(up => `${up}_any`),
            'any_any'
        ];
    }
}
